import { create } from 'zustand';

const useLookupsStore = create((set, get) => ({
  // State
  tables: [],
  currentTable: null,
  entries: [],
  isLoading: false,
  error: null,
  hasProfileId: false,
  hasIsActive: false,

  // Actions
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  // Fetch all lookup tables metadata
  fetchTables: async (accessToken) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch('/api/lookups/tables', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch lookup tables');
      }

      set({
        tables: data.tables || [],
        isLoading: false,
      });

      return { success: true, tables: data.tables };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Fetch entries from a specific lookup table
  fetchEntries: async (tableName, accessToken) => {
    // Clear old entries immediately when switching tables to prevent showing wrong data
    const currentTable = get().currentTable;
    if (currentTable !== tableName) {
      set({ entries: [], currentTable: tableName });
    }

    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`/api/lookups/${tableName}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch lookup entries');
      }

      set({
        currentTable: tableName,
        entries: data.entries || [],
        hasProfileId: data.has_profile_id || false,
        hasIsActive: data.has_is_active || false,
        isLoading: false,
      });

      return { success: true, entries: data.entries };
    } catch (error) {
      set({
        isLoading: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  },

  // Create new lookup entry
  createEntry: async (tableName, label, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/lookups/${tableName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ label }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create lookup entry');
      }

      // Add new entry to list
      set((state) => ({
        entries: [...state.entries, data.entry].sort((a, b) => a.sort_order - b.sort_order),
      }));

      return { success: true, entry: data.entry };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Update lookup entry
  updateEntry: async (tableName, id, updates, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/lookups/${tableName}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update lookup entry');
      }

      // Update entry in list
      set((state) => ({
        entries: state.entries.map((e) =>
          e.id === id ? data.entry : e
        ),
      }));

      return { success: true, entry: data.entry };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Delete lookup entry
  deleteEntry: async (tableName, id, accessToken) => {
    set({ error: null });

    try {
      const response = await fetch(`/api/lookups/${tableName}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete lookup entry');
      }

      // Remove entry from list
      set((state) => ({
        entries: state.entries.filter((e) => e.id !== id),
      }));

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Reorder lookup entries
  reorderEntries: async (tableName, order, accessToken) => {
    try {
      const response = await fetch(`/api/lookups/${tableName}/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ order }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reorder lookup entries');
      }

      return { success: true };
    } catch (error) {
      set({ error: error.message });
      return { success: false, error: error.message };
    }
  },
}));

export { useLookupsStore };
